import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Link as LinkT, LinkSortKey, LinkStats } from "shared";
import {
	deleteLink,
	listLinks,
	updateLink,
	type ArchivedFilter,
} from "@/api/links";
import { ApiException } from "@/api/client";
import { SortHeader, type SortState } from "@/components/SortHeader";
import {
	copyText,
	formatDt,
	shortUrl,
	statusClass,
	statusLabel,
} from "@/lib/format";

export function LinksPage() {
	const [links, setLinks] = useState<LinkT[]>([]);
	const [stats, setStats] = useState<LinkStats | null>(null);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [q, setQ] = useState("");
	const [search, setSearch] = useState("");
	const [archived, setArchived] = useState<ArchivedFilter>("false");
	const [sort, setSort] = useState<SortState<LinkSortKey>>({
		key: "createdAt",
		dir: "desc",
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [copied, setCopied] = useState<string | null>(null);

	const load = useCallback(
		async (opts?: { append?: boolean; cursor?: string | null }) => {
			setLoading(true);
			setError(null);
			try {
				const res = await listLinks({
					q: search || undefined,
					cursor: opts?.cursor ?? undefined,
					archived,
					sort: sort.key,
					order: sort.dir,
				});
				setLinks((prev) =>
					opts?.append ? [...prev, ...res.links] : res.links,
				);
				setNextCursor(res.nextCursor);
				setStats(res.stats);
			} catch (err) {
				setError(
					err instanceof ApiException
						? err.message
						: "Failed to load links",
				);
			} finally {
				setLoading(false);
			}
		},
		[search, archived, sort],
	);

	useEffect(() => {
		void load();
	}, [load]);

	async function onToggle(link: LinkT) {
		try {
			const { link: updated } = await updateLink(link.id, {
				active: !link.active,
			});
			setLinks((prev) =>
				prev.map((l) => (l.id === updated.id ? updated : l)),
			);
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Update failed",
			);
		}
	}

	async function onDelete(link: LinkT) {
		if (!confirm(`Delete /${link.hash}? This cannot be undone.`)) return;
		try {
			await deleteLink(link.id);
			setLinks((prev) => prev.filter((l) => l.id !== link.id));
			if (stats) {
				setStats({
					totalLinks: Math.max(0, stats.totalLinks - 1),
					totalClicks: Math.max(
						0,
						stats.totalClicks - link.clickCount,
					),
				});
			}
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Delete failed",
			);
		}
	}

	async function onCopy(hash: string) {
		const ok = await copyText(shortUrl(hash));
		if (ok) {
			setCopied(hash);
			setTimeout(() => setCopied(null), 1500);
		}
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="text-xl font-semibold tracking-tight">
						Links
					</h1>
					{stats && (
						<p className="mt-1 text-sm text-fg-muted">
							{stats.totalLinks} links · {stats.totalClicks} clicks
						</p>
					)}
				</div>
				<div className="flex flex-wrap gap-2">
					<Link
						to="/links/import"
						className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
					>
						Import
					</Link>
					<Link
						to="/links/new"
						className="rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600"
					>
						New link
					</Link>
				</div>
			</div>

			<form
				className="flex flex-wrap gap-2"
				onSubmit={(e) => {
					e.preventDefault();
					setSearch(q.trim());
				}}
			>
				<input
					type="search"
					placeholder="Search hash or URL…"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					className="flex-1 rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40"
				/>
				<button
					type="submit"
					className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
				>
					Search
				</button>
				<select
					value={archived}
					onChange={(e) => setArchived(e.target.value as ArchivedFilter)}
					className="rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40"
					aria-label="Archived filter"
				>
					<option value="false">Active</option>
					<option value="true">Archived</option>
					<option value="all">All</option>
				</select>
			</form>

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			<div className="overflow-x-auto rounded-xl border border-border">
				<table className="w-full min-w-[640px] text-left text-sm">
					<thead className="border-b border-border bg-surface-raised text-fg-muted">
						<tr>
							<SortHeader
								label="Hash"
								sortKey="hash"
								sort={sort}
								onSort={setSort}
								defaultDir="asc"
							/>
							<SortHeader
								label="Target"
								sortKey="targetUrl"
								sort={sort}
								onSort={setSort}
								defaultDir="asc"
							/>
							<th className="px-3 py-2 font-medium">Status</th>
							<SortHeader
								label="Clicks"
								sortKey="clicks"
								sort={sort}
								onSort={setSort}
								defaultDir="desc"
							/>
							<SortHeader
								label="Created"
								sortKey="createdAt"
								sort={sort}
								onSort={setSort}
								defaultDir="desc"
							/>
							<th className="px-3 py-2 font-medium">Actions</th>
						</tr>
					</thead>
					<tbody>
						{links.map((link) => (
							<tr
								key={link.id}
								className="border-b border-border last:border-0"
							>
								<td className="px-3 py-2 font-mono">
									<button
										type="button"
										className="text-accent hover:underline"
										onClick={() => void onCopy(link.hash)}
										title="Copy short URL"
									>
										/{link.hash}
									</button>
									{copied === link.hash && (
										<span className="ml-1 text-xs text-fg-muted">
											copied
										</span>
									)}
								</td>
								<td className="max-w-[14rem] truncate px-3 py-2 text-fg-muted">
									<a
										href={link.targetUrl}
										target="_blank"
										rel="noreferrer"
										className="hover:text-fg hover:underline"
									>
										{link.targetUrl}
									</a>
								</td>
								<td className="px-3 py-2">
									<div className="flex flex-wrap items-center gap-1">
										<span
											className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(link.status)}`}
										>
											{statusLabel(link.status)}
										</span>
										{link.archived && (
											<span className="rounded-full bg-indigo-500/15 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-400">
												Archived
											</span>
										)}
									</div>
								</td>
								<td className="px-3 py-2 font-mono">
									{link.clickCount}
								</td>
								<td className="px-3 py-2 text-fg-muted text-xs">
									{formatDt(link.createdAt)}
								</td>
								<td className="px-3 py-2">
									<div className="flex flex-wrap gap-2 text-xs">
										<Link
											to={`/links/${link.id}`}
											className="text-accent hover:underline"
										>
											Open
										</Link>
										<button
											type="button"
											className="text-fg-muted hover:text-fg"
											onClick={() => void onToggle(link)}
										>
											{link.active ? "Disable" : "Enable"}
										</button>
										<button
											type="button"
											className="text-rose-600 hover:underline dark:text-rose-400"
											onClick={() => void onDelete(link)}
										>
											Delete
										</button>
									</div>
								</td>
							</tr>
						))}
						{!loading && links.length === 0 && (
							<tr>
								<td
									colSpan={6}
									className="px-3 py-8 text-center text-fg-muted"
								>
									No links yet.{" "}
									<Link
										to="/links/new"
										className="text-accent hover:underline"
									>
										Create one
									</Link>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{loading && (
				<p className="text-sm text-fg-muted">Loading…</p>
			)}

			{nextCursor && !loading && (
				<button
					type="button"
					className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
					onClick={() => {
						void load({ append: true, cursor: nextCursor });
					}}
				>
					Load more
				</button>
			)}
		</div>
	);
}
